import { useState, useEffect, useCallback, useRef } from "react";
import axiosInstance from "../../api/axiosInstance";

const TOAST_DURATION = 4000;

const approvalApi = {
  list: (vendorProfileId) =>
    axiosInstance.get(`/api/superadmin/vendors/${vendorProfileId}/approval-settings/`),
  get: (vendorProfileId, userId) =>
    axiosInstance.get(`/api/superadmin/vendors/${vendorProfileId}/approval-settings/${userId}/`),
  patchPriority: (vendorProfileId, userId, body) =>
    axiosInstance.patch(`/api/superadmin/vendors/${vendorProfileId}/approval-settings/${userId}/`, body),
  patchSignature: (vendorProfileId, userId, formData) =>
    axiosInstance.patch(`/api/superadmin/vendors/${vendorProfileId}/approval-settings/${userId}/`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
};

let _asToastSetter = null;
const toast = {
  _push: (item) => {
    const id = Date.now() + Math.random();
    _asToastSetter?.((p) => [...p, { id, ...item }]);
    return id;
  },
  success: (title, msg) => toast._push({ type: "success", title, msg }),
  error:   (title, msg) => toast._push({ type: "error",   title, msg }),
  info:    (title, msg) => toast._push({ type: "info",    title, msg }),
  loading: (title)      => toast._push({ type: "loading", title, persistent: true }),
  dismiss: (id)         => _asToastSetter?.((p) => p.filter((t) => t.id !== id)),
};
const TOAST_META = {
  success: { bar: "#16a34a", icon: "✓" },
  error:   { bar: "#dc2626", icon: "✕" },
  info:    { bar: "#1d4ed8", icon: "i" },
  loading: { bar: "#94a3b8", icon: null },
};

const CloseIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
const LoaderIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    style={{ animation: "as-spin .8s linear infinite", flexShrink: 0 }}>
    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
  </svg>
);
const RefreshIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
);
const EyeIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
  </svg>
);
const UploadIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="16 16 12 12 8 16" /><line x1="12" y1="12" x2="12" y2="21" />
    <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
  </svg>
);
const UserIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
  </svg>
);
const ShieldIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);
const StarIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);
const CheckIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
const XIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
const FileIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
  </svg>
);
const LinkIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
);
const AlertTriangleIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);
const TrashIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </svg>
);

const ToastItem = ({ t, onDismiss }) => {
  const meta = TOAST_META[t.type];
  const [progress, setProgress] = useState(100);
  const [exiting, setExiting]   = useState(false);
  const rafRef  = useRef(null);
  const startTs = useRef(Date.now());
  const dismiss = useCallback(() => {
    setExiting(true);
    setTimeout(() => onDismiss(t.id), 280);
  }, [t.id, onDismiss]);
  useEffect(() => {
    if (t.persistent) return;
    const tick = () => {
      const pct = Math.max(0, 100 - ((Date.now() - startTs.current) / TOAST_DURATION) * 100);
      setProgress(pct);
      if (pct > 0) rafRef.current = requestAnimationFrame(tick);
      else dismiss();
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [t.persistent, dismiss]);
  return (
    <div style={{ position: "relative", overflow: "hidden", background: "#fff", borderRadius: 10, borderLeft: `4px solid ${meta.bar}`, boxShadow: "0 4px 20px rgba(0,0,0,.10),0 0 0 1px rgba(0,0,0,.06)", minWidth: 300, maxWidth: 360, animation: exiting ? "as-alOut .28s ease forwards" : "as-alIn .28s cubic-bezier(.34,1.56,.64,1) forwards" }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "13px 14px 14px" }}>
        <div style={{ flexShrink: 0, width: 24, height: 24, borderRadius: "50%", background: `${meta.bar}18`, display: "flex", alignItems: "center", justifyContent: "center", marginTop: 1 }}>
          {t.type === "loading"
            ? <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{ animation: "as-spin .85s linear infinite" }}><circle cx="6.5" cy="6.5" r="5" stroke={meta.bar} strokeWidth="2" strokeDasharray="8 20" strokeLinecap="round" /></svg>
            : <span style={{ fontSize: 11, fontWeight: 800, color: meta.bar, lineHeight: 1 }}>{meta.icon}</span>}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: 0, fontSize: 13.5, fontWeight: 650, color: "#0f172a", lineHeight: 1.35 }}>{t.title}</p>
          {t.msg && <p style={{ margin: "3px 0 0", fontSize: 12.5, color: "#64748b", lineHeight: 1.5 }}>{t.msg}</p>}
        </div>
        <button onClick={dismiss} style={{ background: "none", border: "none", cursor: "pointer", color: "#cbd5e1", padding: 2, flexShrink: 0, display: "flex", borderRadius: 4 }}
          onMouseEnter={(e) => (e.currentTarget.style.color = "#94a3b8")}
          onMouseLeave={(e) => (e.currentTarget.style.color = "#cbd5e1")}>
          <CloseIcon />
        </button>
      </div>
      {!t.persistent && (
        <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 3, background: "#f1f5f9" }}>
          <div style={{ height: "100%", width: `${progress}%`, background: meta.bar, transition: "width .1s linear", borderRadius: "0 2px 0 0" }} />
        </div>
      )}
    </div>
  );
};
const ToastContainer = () => {
  const [toasts, setToasts] = useState([]);
  _asToastSetter = setToasts;
  const dismiss = useCallback((id) => setToasts((p) => p.filter((t) => t.id !== id)), []);
  if (!toasts.length) return null;
  return (
    <div style={{ position: "fixed", bottom: 24, right: 24, zIndex: 9999, display: "flex", flexDirection: "column", gap: 10, alignItems: "flex-end" }}>
      {toasts.map((t) => <ToastItem key={t.id} t={t} onDismiss={dismiss} />)}
    </div>
  );
};

const SkeletonBlock = ({ w, h = 14, r = 6 }) => (
  <div style={{ height: h, width: w, borderRadius: r, background: "linear-gradient(90deg,#f1f5f9 25%,#e2e8f0 50%,#f1f5f9 75%)", backgroundSize: "200% 100%", animation: "as-shimmer 1.4s infinite", flexShrink: 0 }} />
);

const handleApiError = (data) => {
  const detail = data?.data?.detail || data?.data?.message || `Error ${data?.status}`;
  if (data?.status === 401) { toast.error("Session Expired", "Please log in again."); return; }
  if (data?.status === 403) { toast.error("Access Denied", "You don't have permission to perform this action."); return; }
  if (data?.status === 404) { toast.error("Not Found", detail); return; }
  if (data?.status === 400) { toast.error("Validation Failed", detail); return; }
  toast.error("Server Error", `Unexpected error (HTTP ${data?.status}). Please try again.`);
};

const getInitials = (name = "") => {
  const parts = name.trim().split(" ").filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const AVATAR_COLORS = [
  ["#dbeafe", "#1d4ed8"], ["#ede9fe", "#6d28d9"], ["#d1fae5", "#065f46"],
  ["#fef3c7", "#92400e"], ["#fce7f3", "#9d174d"], ["#cffafe", "#0e7490"],
];
const getAvatarColor = (userId) => AVATAR_COLORS[(userId || 0) % AVATAR_COLORS.length];

const MemberBadge = ({ badge }) => {
  if (!badge) return null;
  const isIncluded = badge.tone === "success" || badge.color === "green";
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 10px", borderRadius: 20, fontSize: 11.5, fontWeight: 700, background: isIncluded ? "#dcfce7" : "#fee2e2", color: isIncluded ? "#16a34a" : "#dc2626", border: `1px solid ${isIncluded ? "#bbf7d0" : "#fecaca"}`, whiteSpace: "nowrap" }}>
      {isIncluded ? <CheckIcon /> : <XIcon />}
      {badge.label}
    </span>
  );
};

const InfoRow = ({ icon, label, value, mono, badge }) => (
  <div style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "7px 0", borderBottom: "1px solid #f8fafc" }}>
    <span style={{ color: "#94a3b8", display: "flex", flexShrink: 0, marginTop: 1 }}>{icon}</span>
    <span style={{ fontSize: 12.5, color: "#94a3b8", fontWeight: 550, minWidth: 160, flexShrink: 0 }}>{label}</span>
    {badge || (
      <span style={{ fontSize: 13, color: mono ? "#1e40af" : "#1e293b", fontWeight: mono ? 700 : 500, fontFamily: mono ? "monospace" : "inherit", flex: 1, wordBreak: "break-word" }}>
        {value != null && value !== "" ? value : <span style={{ color: "#cbd5e1" }}>—</span>}
      </span>
    )}
  </div>
);

const SignatureUploadModal = ({ member, vendorProfileId, onClose, onSuccess }) => {
  const [file,      setFile]      = useState(null);
  const [preview,   setPreview]   = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver,  setDragOver]  = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = ""; document.removeEventListener("keydown", onKey); };
  }, [onClose]);

  const handleFile = (f) => {
    if (!f) return;
    const allowed = ["image/jpeg", "image/png", "image/jpg", "image/gif", "image/webp"];
    if (!allowed.includes(f.type)) {
      toast.error("Invalid File Type", "Only JPG, PNG, GIF, or WEBP images are allowed.");
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      toast.error("File Too Large", "Maximum file size is 5 MB.");
      return;
    }
    setFile(f);
    const reader = new FileReader();
    reader.onload = (ev) => setPreview(ev.target.result);
    reader.readAsDataURL(f);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) handleFile(dropped);
  };

  const handleUpload = async () => {
    if (!file) { toast.error("No File Selected", "Please select a signature image to upload."); return; }
    setUploading(true);
    const loadId = toast.loading("Uploading signature…");
    try {
      const formData = new FormData();
      formData.append("digital_signature", file);
      const { data } = await approvalApi.patchSignature(vendorProfileId, member.user_id, formData);
      toast.dismiss(loadId);
      if (data?.status >= 400) { handleApiError(data); setUploading(false); return; }
      toast.success("Signature Uploaded", `Digital signature for ${member.name || member.email} uploaded successfully.`);
      onSuccess(data?.data ?? data);
      onClose();
    } catch (err) {
      toast.dismiss(loadId);
      toast.error(
        err?.name === "TypeError" ? "Network Error" : "Upload Failed",
        err?.name === "TypeError" ? "Unable to reach the server. Check your connection." : err.message || "Could not upload the signature."
      );
      setUploading(false);
    }
  };


  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.58)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1200, padding: 20 }}
      onClick={onClose}>
      <div style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 480, boxShadow: "0 24px 64px rgba(0,0,0,.22)", animation: "as-fadeUp .2s ease" }}
        onClick={(e) => e.stopPropagation()}>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "18px 24px", borderBottom: "1px solid #f1f5f9" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 34, height: 34, borderRadius: 9, background: "linear-gradient(135deg,#6366f1,#3b82f6)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff" }}>
              <UploadIcon />
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#0f172a" }}>
                {member.digital_signature_url ? "Replace Digital Signature" : "Upload Digital Signature"}
              </h2>
              <p style={{ margin: "2px 0 0", fontSize: 12, color: "#94a3b8" }}>
                For: <strong style={{ color: "#374151" }}>{member.name || member.email}</strong> · JPG, PNG · Max 5 MB
              </p>
            </div>
          </div>
          <button onClick={onClose}
            style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8", display: "flex", padding: 6, borderRadius: 6 }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "#64748b")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "#94a3b8")}>
            <CloseIcon />
          </button>
        </div>

        <div style={{ padding: "20px 24px 24px" }}>
          {member.digital_signature_url && (
            <div style={{ marginBottom: 16, padding: "10px 14px", background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 9, display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 40, height: 28, borderRadius: 5, overflow: "hidden", flexShrink: 0, border: "1px solid #bfdbfe" }}>
                <img src={member.digital_signature_url} alt="Current" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              </div>
              <div style={{ minWidth: 0 }}>
                <p style={{ margin: 0, fontSize: 12, fontWeight: 600, color: "#1d4ed8" }}>Current signature on file</p>
                <p style={{ margin: "1px 0 0", fontSize: 11.5, color: "#64748b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{member.digital_signature_name}</p>
              </div>
            </div>
          )}

          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            style={{ border: `2px dashed ${dragOver ? "#3b82f6" : file ? "#16a34a" : "#e2e8f0"}`, borderRadius: 12, padding: "28px 20px", textAlign: "center", cursor: "pointer", background: dragOver ? "#eff6ff" : file ? "#f0fdf4" : "#fafafa", transition: "all .2s", marginBottom: 14 }}>
            <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/jpg,image/gif,image/webp" style={{ display: "none" }}
              onChange={(e) => handleFile(e.target.files[0])} />

            {preview ? (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
                <img src={preview} alt="Preview" style={{ maxHeight: 120, maxWidth: "100%", borderRadius: 8, objectFit: "contain", border: "1px solid #bbf7d0" }} />
                <p style={{ margin: 0, fontSize: 12.5, fontWeight: 600, color: "#16a34a" }}>{file.name}</p>
                <p style={{ margin: 0, fontSize: 11.5, color: "#94a3b8" }}>{(file.size / 1024).toFixed(0)} KB · Click to change</p>
              </div>
            ) : (
              <>
                <div style={{ width: 48, height: 48, borderRadius: "50%", background: "#e2e8f0", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px", color: "#94a3b8" }}>
                  <UploadIcon />
                </div>
                <p style={{ margin: "0 0 4px", fontSize: 14, fontWeight: 600, color: "#374151" }}>Click or drag & drop</p>
                <p style={{ margin: 0, fontSize: 12.5, color: "#94a3b8" }}>Supported: JPG, PNG, GIF, WEBP</p>
              </>
            )}
          </div>

          {file && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", background: "#f0fdf4", borderRadius: 8, border: "1px solid #bbf7d0", marginBottom: 16 }}>
              <FileIcon />
              <span style={{ fontSize: 12.5, fontWeight: 600, color: "#166534", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{file.name}</span>
              <button onClick={(e) => { e.stopPropagation(); setFile(null); setPreview(null); }}
                style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8", display: "flex", padding: 2 }}>
                <CloseIcon />
              </button>
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <button onClick={onClose} disabled={uploading}
              style={{ padding: "9px 20px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13.5, fontWeight: 600, cursor: uploading ? "not-allowed" : "pointer", background: "#fff", color: "#374151" }}
              onMouseEnter={(e) => { if (!uploading) e.currentTarget.style.background = "#f8fafc"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; }}>
              Cancel
            </button>
            <button onClick={handleUpload} disabled={uploading || !file}
              style={{ padding: "9px 22px", border: "none", borderRadius: 8, fontSize: 13.5, fontWeight: 600, cursor: (uploading || !file) ? "not-allowed" : "pointer", background: (uploading || !file) ? "#93c5fd" : "#3b82f6", color: "#fff", display: "flex", alignItems: "center", gap: 7, transition: "background .15s" }}
              onMouseEnter={(e) => { if (!uploading && file) e.currentTarget.style.background = "#2563eb"; }}
              onMouseLeave={(e) => { if (!uploading && file) e.currentTarget.style.background = "#3b82f6"; }}>
              {uploading ? <><LoaderIcon size={14} /> Uploading…</> : <><UploadIcon /> Upload Signature</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const PriorityModal = ({ member, vendorProfileId, onClose, onSuccess }) => {
  const isCurrentlyIncluded = member.included_in_approval_flow;
  const [priority, setPriority] = useState(
    isCurrentlyIncluded && member.approval_priority_input > 0
      ? String(member.approval_priority_input)
      : ""
  );
  const [removing, setRemoving] = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState("");

  useEffect(() => {
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = ""; document.removeEventListener("keydown", onKey); };
  }, [onClose]);

  const validate = () => {
    const val = parseInt(priority, 10);
    if (!priority.trim()) { setError("Priority is required."); return false; }
    if (isNaN(val) || val < 1) { setError("Priority must be a positive number (1 or higher)."); return false; }
    setError("");
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    const loadId = toast.loading("Updating approval priority…");
    try {
      const { data } = await approvalApi.patchPriority(vendorProfileId, member.user_id, {
        approval_priority: parseInt(priority, 10),
      });
      toast.dismiss(loadId);
      if (data?.status >= 400) { handleApiError(data); setSaving(false); return; }
      toast.success(
        isCurrentlyIncluded ? "Priority Updated" : "Added to Approval Flow",
        isCurrentlyIncluded
          ? `${member.name || member.email}'s approval priority set to ${priority}.`
          : `${member.name || member.email} has been added to the approval flow with priority ${priority}.`
      );
      onSuccess(data?.data ?? data);
      onClose();
    } catch (err) {
      toast.dismiss(loadId);
      toast.error(
        err?.name === "TypeError" ? "Network Error" : "Update Failed",
        err?.name === "TypeError" ? "Unable to reach the server." : "Could not update priority."
      );
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    setRemoving(true);
    const loadId = toast.loading("Removing from approval flow…");
    try {
      const { data } = await approvalApi.patchPriority(vendorProfileId, member.user_id, {
        approval_priority: null,
      });
      toast.dismiss(loadId);
      if (data?.status >= 400) { handleApiError(data); setRemoving(false); return; }
      toast.success("Removed from Flow", `${member.name || member.email} has been removed from the approval flow.`);
      onSuccess(data?.data ?? data);
      onClose();
    } catch (err) {
      toast.dismiss(loadId);
      toast.error(
        err?.name === "TypeError" ? "Network Error" : "Remove Failed",
        err?.name === "TypeError" ? "Unable to reach the server." : "Could not remove from flow."
      );
      setRemoving(false);
    }
  };

  const [bg, tc] = getAvatarColor(member.user_id);

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.58)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1200, padding: 20 }}
      onClick={onClose}>
      <div style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 440, boxShadow: "0 24px 64px rgba(0,0,0,.22)", animation: "as-fadeUp .2s ease" }}
        onClick={(e) => e.stopPropagation()}>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "18px 24px", borderBottom: "1px solid #f1f5f9" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 34, height: 34, borderRadius: 9, background: "linear-gradient(135deg,#f59e0b,#d97706)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff" }}>
              <StarIcon />
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#0f172a" }}>
                {isCurrentlyIncluded ? "Update Approval Priority" : "Add to Approval Flow"}
              </h2>
              <p style={{ margin: "2px 0 0", fontSize: 12, color: "#94a3b8" }}>
                {member.name || member.email} · {member.designation || "Member"}
              </p>
            </div>
          </div>
          <button onClick={onClose}
            style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8", display: "flex", padding: 6, borderRadius: 6 }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "#64748b")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "#94a3b8")}>
            <CloseIcon />
          </button>
        </div>

        <div style={{ padding: "22px 24px 24px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", background: "#f8fafc", borderRadius: 10, border: "1px solid #e2e8f0", marginBottom: 20 }}>
            <div style={{ width: 40, height: 40, borderRadius: "50%", background: bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 800, color: tc, flexShrink: 0 }}>
              {getInitials(member.name || member.email)}
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <p style={{ margin: 0, fontSize: 13.5, fontWeight: 700, color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {member.name || "—"}
              </p>
              <p style={{ margin: "2px 0 0", fontSize: 12, color: "#64748b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{member.email}</p>
            </div>
            <MemberBadge badge={member.approval_member_badge} />
          </div>

          <div style={{ marginBottom: 20 }}>
            <label style={{ fontSize: 12.5, fontWeight: 650, color: "#374151", display: "flex", alignItems: "center", gap: 4, marginBottom: 6 }}>
              Approval Priority <span style={{ color: "#dc2626" }}>*</span>
              <span style={{ fontSize: 11, color: "#94a3b8", fontWeight: 400 }}>(lower number = higher priority)</span>
            </label>
            <input
              type="number"
              min="1"
              step="1"
              value={priority}
              onChange={(e) => { setPriority(e.target.value); if (error) setError(""); }}
              placeholder="e.g. 1"
              style={{ width: "100%", padding: "10px 14px", border: `1px solid ${error ? "#fca5a5" : "#e2e8f0"}`, borderRadius: 9, fontSize: 15, fontWeight: 700, background: error ? "#fff5f5" : "#fff", color: "#1e293b", outline: "none", fontFamily: "inherit", boxSizing: "border-box", transition: "border-color .15s" }}
              onFocus={(e) => (e.target.style.borderColor = "#f59e0b")}
              onBlur={(e) => (e.target.style.borderColor = error ? "#fca5a5" : "#e2e8f0")}
              onKeyDown={(e) => { if (e.key === "Enter") handleSave(); }}
            />
            {error && (
              <span style={{ fontSize: 11.5, color: "#dc2626", display: "flex", alignItems: "center", gap: 4, marginTop: 4 }}>
                <AlertTriangleIcon size={12} /> {error}
              </span>
            )}
            <p style={{ margin: "6px 0 0", fontSize: 11.5, color: "#94a3b8" }}>
              Priority 1 means first approver. Each member must have a unique priority.
            </p>
          </div>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", justifyContent: isCurrentlyIncluded ? "space-between" : "flex-end" }}>
            {isCurrentlyIncluded && (
              <button onClick={handleRemove} disabled={removing || saving}
                style={{ display: "flex", alignItems: "center", gap: 6, padding: "9px 16px", border: "1px solid #fecaca", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: (removing || saving) ? "not-allowed" : "pointer", background: "#fff5f5", color: "#dc2626", transition: "background .15s" }}
                onMouseEnter={(e) => { if (!removing && !saving) e.currentTarget.style.background = "#fee2e2"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "#fff5f5"; }}>
                {removing ? <><LoaderIcon size={13} /> Removing…</> : <><TrashIcon /> Remove from Flow</>}
              </button>
            )}
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={onClose} disabled={saving || removing}
                style={{ padding: "9px 20px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13.5, fontWeight: 600, cursor: (saving || removing) ? "not-allowed" : "pointer", background: "#fff", color: "#374151" }}
                onMouseEnter={(e) => { if (!saving && !removing) e.currentTarget.style.background = "#f8fafc"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; }}>
                Cancel
              </button>
              <button onClick={handleSave} disabled={saving || removing}
                style={{ padding: "9px 22px", border: "none", borderRadius: 8, fontSize: 13.5, fontWeight: 600, cursor: (saving || removing) ? "not-allowed" : "pointer", background: (saving || removing) ? "#fcd34d" : "#f59e0b", color: "#fff", display: "flex", alignItems: "center", gap: 7, transition: "background .15s" }}
                onMouseEnter={(e) => { if (!saving && !removing) e.currentTarget.style.background = "#d97706"; }}
                onMouseLeave={(e) => { if (!saving && !removing) e.currentTarget.style.background = "#f59e0b"; }}>
                {saving ? <><LoaderIcon size={14} /> Saving…</> : <><CheckIcon /> {isCurrentlyIncluded ? "Update Priority" : "Add to Flow"}</>}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ViewDetailModal = ({ member, vendorProfileId, onClose, onEditPriority, onUploadSignature }) => {
  const [detail,  setDetail]  = useState(member);
  const [loading, setLoading] = useState(false);
  const [sigPreview, setSigPreview] = useState(false);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = ""; document.removeEventListener("keydown", onKey); };
  }, [onClose]);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await approvalApi.get(vendorProfileId, member.user_id);
      if (data?.status >= 400) { handleApiError(data); return; }
      setDetail(data?.data ?? detail);
    } catch (err) {
      toast.error(
        err?.name === "TypeError" ? "Network Error" : "Refresh Failed",
        "Could not reload member details."
      );
    } finally {
      setLoading(false);
    }
  };

  if (!detail) return null;
  const [bg, tc] = getAvatarColor(detail.user_id);
  const isIncluded = detail.included_in_approval_flow;

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }}
      onClick={onClose}>
      <div style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 580, boxShadow: "0 24px 64px rgba(0,0,0,.22)", animation: "as-fadeUp .2s ease", display: "flex", flexDirection: "column", maxHeight: "92vh" }}
        onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "18px 24px", borderBottom: "1px solid #f1f5f9", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 44, height: 44, borderRadius: "50%", background: bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, fontWeight: 800, color: tc, flexShrink: 0 }}>
              {getInitials(detail.name || detail.email)}
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: 15.5, fontWeight: 700, color: "#0f172a" }}>{detail.name || detail.email}</h2>
              <p style={{ margin: "2px 0 0", fontSize: 12, color: "#94a3b8" }}>{detail.email}</p>
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button onClick={refresh} disabled={loading}
              style={{ display: "flex", alignItems: "center", gap: 5, padding: "6px 12px", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: loading ? "not-allowed" : "pointer", color: "#374151" }}>
              {loading ? <LoaderIcon size={12} /> : <RefreshIcon />}
            </button>
            <button onClick={onClose}
              style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8", display: "flex", padding: 6, borderRadius: 6 }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "#64748b")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "#94a3b8")}>
              <CloseIcon />
            </button>
          </div>
        </div>

        <div style={{ padding: "20px 24px 24px", overflowY: "auto", flex: 1 }}>
          <div style={{ padding: "12px 16px", borderRadius: 10, background: isIncluded ? "#f0fdf4" : "#fff5f5", border: `1px solid ${isIncluded ? "#bbf7d0" : "#fecaca"}`, marginBottom: 18, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <ShieldIcon />
              <span style={{ fontSize: 13.5, fontWeight: 700, color: isIncluded ? "#16a34a" : "#dc2626" }}>
                {isIncluded ? `In Approval Flow — Priority ${detail.approval_priority}` : "Not in Approval Flow"}
              </span>
            </div>
            <MemberBadge badge={detail.approval_member_badge} />
          </div>

          <InfoRow icon={<UserIcon />}    label="Full Name"           value={detail.name} />
          <InfoRow icon={<UserIcon />}    label="Email"               value={detail.email} mono />
          <InfoRow icon={<UserIcon />}    label="Designation"         value={detail.designation} />
          <InfoRow icon={<StarIcon />}    label="Approval Priority"
            badge={
              detail.approval_priority != null
                ? <span style={{ fontFamily: "monospace", fontSize: 13, fontWeight: 800, color: "#1e40af" }}>#{detail.approval_priority}</span>
                : <span style={{ color: "#cbd5e1", fontSize: 13 }}>—</span>
            }
          />
          <InfoRow icon={<ShieldIcon />}  label="Is Approver"
            badge={
              <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 9px", borderRadius: 20, fontSize: 11.5, fontWeight: 700, background: detail.is_approver ? "#dcfce7" : "#f8fafc", color: detail.is_approver ? "#16a34a" : "#94a3b8", border: `1px solid ${detail.is_approver ? "#bbf7d0" : "#e2e8f0"}` }}>
                {detail.is_approver ? <CheckIcon /> : <XIcon />}
                {detail.is_approver ? "Yes" : "No"}
              </span>
            }
          />
          <InfoRow icon={<UserIcon />}    label="Owner"
            badge={
              detail.is_owner
                ? <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 9px", borderRadius: 20, fontSize: 11.5, fontWeight: 700, background: "#fef3c7", color: "#d97706", border: "1px solid #fde68a" }}>
                    <StarIcon /> Owner
                  </span>
                : <span style={{ color: "#cbd5e1", fontSize: 13 }}>—</span>
            }
          />
          <InfoRow icon={<UserIcon />}    label="Account Active"
            badge={
              <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 9px", borderRadius: 20, fontSize: 11.5, fontWeight: 700, background: detail.is_active ? "#dcfce7" : "#fee2e2", color: detail.is_active ? "#16a34a" : "#dc2626", border: `1px solid ${detail.is_active ? "#bbf7d0" : "#fecaca"}` }}>
                {detail.is_active ? <CheckIcon /> : <XIcon />}
                {detail.is_active ? "Active" : "Inactive"}
              </span>
            }
          />

          <div style={{ marginTop: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <p style={{ margin: 0, fontSize: 12.5, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: ".05em" }}>Digital Signature</p>
              <button onClick={() => { onClose(); onUploadSignature(detail); }}
                style={{ display: "flex", alignItems: "center", gap: 5, padding: "5px 12px", background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 7, fontSize: 12, fontWeight: 600, color: "#2563eb", cursor: "pointer" }}>
                <UploadIcon /> {detail.digital_signature_url ? "Replace" : "Upload"}
              </button>
            </div>

            {detail.digital_signature_url ? (
              <div style={{ border: "1px solid #e2e8f0", borderRadius: 10, overflow: "hidden" }}>
                <div style={{ position: "relative", height: 140, background: "#f8fafc", cursor: "pointer" }}
                  onClick={() => setSigPreview(true)}>
                  <img src={detail.digital_signature_url} alt="Digital Signature"
                    style={{ width: "100%", height: "100%", objectFit: "contain", padding: "8px" }}
                    onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }} />
                  <div style={{ display: "none", height: "100%", alignItems: "center", justifyContent: "center", color: "#94a3b8" }}>
                    <FileIcon />
                  </div>
                  <div style={{ position: "absolute", inset: 0, background: "rgba(15,23,42,.3)", display: "flex", alignItems: "center", justifyContent: "center", opacity: 0, transition: "opacity .2s" }}
                    onMouseEnter={(e) => (e.currentTarget.style.opacity = 1)}
                    onMouseLeave={(e) => (e.currentTarget.style.opacity = 0)}>
                    <span style={{ background: "rgba(255,255,255,.9)", borderRadius: 8, padding: "6px 14px", fontSize: 12, fontWeight: 700, color: "#0f172a", display: "flex", alignItems: "center", gap: 6 }}>
                      <EyeIcon /> Preview
                    </span>
                  </div>
                </div>
                <div style={{ padding: "10px 14px", display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: "1px solid #f1f5f9", flexWrap: "wrap", gap: 8 }}>
                  <div style={{ minWidth: 0 }}>
                    <p style={{ margin: 0, fontSize: 12.5, fontWeight: 600, color: "#374151", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{detail.digital_signature_name}</p>
                    {detail.uploaded_at && (
                      <p style={{ margin: "2px 0 0", fontSize: 11.5, color: "#94a3b8" }}>
                        Uploaded: {new Date(detail.uploaded_at).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                        {detail.uploaded_by?.email ? ` by ${detail.uploaded_by.email}` : ""}
                      </p>
                    )}
                  </div>
                  <a href={detail.digital_signature_url} target="_blank" rel="noreferrer"
                    style={{ display: "flex", alignItems: "center", gap: 5, padding: "5px 12px", background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 7, color: "#2563eb", fontSize: 12, fontWeight: 600, textDecoration: "none", whiteSpace: "nowrap" }}>
                    <LinkIcon /> Open
                  </a>
                </div>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "28px 20px", background: "#f8fafc", borderRadius: 10, border: "1px dashed #e2e8f0", gap: 8, textAlign: "center" }}>
                <div style={{ width: 44, height: 44, borderRadius: "50%", background: "#e2e8f0", display: "flex", alignItems: "center", justifyContent: "center", color: "#94a3b8" }}>
                  <FileIcon />
                </div>
                <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: "#374151" }}>No Signature Uploaded</p>
                <p style={{ margin: 0, fontSize: 12, color: "#94a3b8" }}>Upload a digital signature to complete this approver's profile.</p>
              </div>
            )}
          </div>

          <div style={{ display: "flex", gap: 10, marginTop: 20, flexWrap: "wrap" }}>
            <button onClick={() => { onClose(); onEditPriority(detail); }}
              style={{ flex: "1 1 auto", display: "flex", alignItems: "center", justifyContent: "center", gap: 7, padding: "10px 16px", background: "#fef9c3", border: "1px solid #fde68a", borderRadius: 9, fontSize: 13.5, fontWeight: 600, cursor: "pointer", color: "#b45309", transition: "background .15s" }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "#fef08a")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "#fef9c3")}>
              <StarIcon /> {isIncluded ? "Edit Priority" : "Add to Flow"}
            </button>
            <button onClick={() => { onClose(); onUploadSignature(detail); }}
              style={{ flex: "1 1 auto", display: "flex", alignItems: "center", justifyContent: "center", gap: 7, padding: "10px 16px", background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 9, fontSize: 13.5, fontWeight: 600, cursor: "pointer", color: "#2563eb", transition: "background .15s" }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "#dbeafe")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "#eff6ff")}>
              <UploadIcon /> {detail.digital_signature_url ? "Replace Signature" : "Upload Signature"}
            </button>
          </div>
        </div>
      </div>

      {sigPreview && detail.digital_signature_url && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.88)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 3000, padding: 24 }}
          onClick={() => setSigPreview(false)}>
          <div style={{ position: "relative", maxWidth: "90vw", maxHeight: "90vh" }} onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setSigPreview(false)}
              style={{ position: "absolute", top: -14, right: -14, width: 32, height: 32, borderRadius: "50%", background: "#fff", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 8px rgba(0,0,0,.3)", zIndex: 1 }}>
              <CloseIcon />
            </button>
            <img src={detail.digital_signature_url} alt="Signature Preview"
              style={{ maxWidth: "88vw", maxHeight: "80vh", borderRadius: 12, objectFit: "contain", boxShadow: "0 24px 64px rgba(0,0,0,.5)", background: "#fff", padding: 12 }} />
          </div>
        </div>
      )}
    </div>
  );
};

const MemberRow = ({ member, index, onView, onEditPriority, onUploadSignature }) => {
  const [bg, tc] = getAvatarColor(member.user_id);
  const isIncluded = member.included_in_approval_flow;

  return (
    <tr className="as-row" style={{ borderBottom: "1px solid #f1f5f9", transition: "background .12s", cursor: "pointer" }}
      onClick={() => onView(member)}>

      <td style={{ padding: "13px 16px", color: "#94a3b8", fontSize: "12.5", whiteSpace: "nowrap" }}>
        {index + 1}
      </td>

      <td style={{ padding: "13px 16px", minWidth: 200 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 36, height: 36, borderRadius: "50%", background: bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 800, color: tc, flexShrink: 0 }}>
            {getInitials(member.name || member.email)}
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 700, color: "#0f172a", fontSize: 13.5, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
              {member.name || "—"}
              {member.is_owner && (
                <span style={{ fontSize: 10, fontWeight: 700, background: "#fef3c7", color: "#d97706", padding: "1px 6px", borderRadius: 4, border: "1px solid #fde68a" }}>OWNER</span>
              )}
            </div>
            <div style={{ fontSize: 12, color: "#64748b", marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 200 }}>{member.email}</div>
          </div>
        </div>
      </td>

      <td style={{ padding: "13px 16px" }}>
        <span style={{ fontSize: 13, color: "#475569" }}>{member.designation || <span style={{ color: "#cbd5e1" }}>—</span>}</span>
      </td>

      <td style={{ padding: "13px 16px", whiteSpace: "nowrap" }}>
        <MemberBadge badge={member.approval_member_badge} />
      </td>

      <td style={{ padding: "13px 16px", whiteSpace: "nowrap" }}>
        {isIncluded && member.approval_priority != null ? (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 10px", borderRadius: 8, fontSize: 12.5, fontWeight: 800, background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe", fontFamily: "monospace" }}>
            # {member.approval_priority}
          </span>
        ) : (
          <span style={{ fontSize: 12.5, color: "#cbd5e1" }}>—</span>
        )}
      </td>

      <td style={{ padding: "13px 16px", whiteSpace: "nowrap" }}>
        {member.digital_signature_url ? (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 10px", borderRadius: 20, fontSize: 11.5, fontWeight: 700, background: "#dcfce7", color: "#16a34a", border: "1px solid #bbf7d0" }}>
            <CheckIcon /> Uploaded
          </span>
        ) : (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 10px", borderRadius: 20, fontSize: 11.5, fontWeight: 700, background: "#f8fafc", color: "#94a3b8", border: "1px solid #e2e8f0" }}>
            Not Uploaded
          </span>
        )}
      </td>

      <td style={{ padding: "13px 16px", whiteSpace: "nowrap" }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <button title="View Details" onClick={() => onView(member)} className="as-action-btn"
            style={{ display: "flex", alignItems: "center", padding: "5px 8px", background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 6, cursor: "pointer", color: "#3b82f6", transition: "opacity .15s" }}>
            <EyeIcon />
          </button>
          <button title={isIncluded ? "Edit Priority" : "Add to Flow"} onClick={() => onEditPriority(member)} className="as-action-btn"
            style={{ display: "flex", alignItems: "center", padding: "5px 8px", background: "#fef9c3", border: "1px solid #fde68a", borderRadius: 6, cursor: "pointer", color: "#b45309", transition: "opacity .15s" }}>
            <StarIcon />
          </button>
          <button title={member.digital_signature_url ? "Replace Signature" : "Upload Signature"} onClick={() => onUploadSignature(member)} className="as-action-btn"
            style={{ display: "flex", alignItems: "center", padding: "5px 8px", background: "#f5f3ff", border: "1px solid #ddd6fe", borderRadius: 6, cursor: "pointer", color: "#6d28d9", transition: "opacity .15s" }}>
            <UploadIcon />
          </button>
        </div>
      </td>
    </tr>
  );
};

const MemberCard = ({ member, onView, onEditPriority, onUploadSignature }) => {
  const [bg, tc] = getAvatarColor(member.user_id);
  const isIncluded = member.included_in_approval_flow;
  return (
    <div className="as-card" onClick={() => onView(member)}
      style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: "16px", cursor: "pointer", transition: "box-shadow .15s, border-color .15s", boxShadow: "0 1px 3px rgba(0,0,0,.04)", position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: isIncluded ? "#16a34a" : "#e2e8f0", borderRadius: "12px 12px 0 0" }} />

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12, paddingTop: 4 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flex: 1, minWidth: 0 }}>
          <div style={{ width: 40, height: 40, borderRadius: "50%", background: bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 800, color: tc, flexShrink: 0 }}>
            {getInitials(member.name || member.email)}
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 700, color: "#0f172a", fontSize: 14, display: "flex", alignItems: "center", gap: 5, flexWrap: "wrap" }}>
              {member.name || "—"}
              {member.is_owner && <span style={{ fontSize: 10, fontWeight: 700, background: "#fef3c7", color: "#d97706", padding: "1px 5px", borderRadius: 4, border: "1px solid #fde68a" }}>OWNER</span>}
            </div>
            <div style={{ fontSize: 12, color: "#64748b", marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{member.email}</div>
            {member.designation && <div style={{ fontSize: 11.5, color: "#94a3b8", marginTop: 1 }}>{member.designation}</div>}
          </div>
        </div>
        <MemberBadge badge={member.approval_member_badge} />
      </div>

      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", paddingTop: 10, borderTop: "1px solid #f1f5f9" }}>
        {isIncluded && member.approval_priority != null && (
          <span style={{ fontSize: 12, fontWeight: 700, background: "#eff6ff", color: "#1d4ed8", padding: "2px 8px", borderRadius: 6, border: "1px solid #bfdbfe", fontFamily: "monospace" }}>
            Priority #{member.approval_priority}
          </span>
        )}
        {member.digital_signature_url && (
          <span style={{ fontSize: 11.5, fontWeight: 700, background: "#dcfce7", color: "#16a34a", padding: "2px 8px", borderRadius: 6, border: "1px solid #bbf7d0", display: "flex", alignItems: "center", gap: 3 }}>
            <CheckIcon /> Signature
          </span>
        )}
        <div style={{ marginLeft: "auto", display: "flex", gap: 6 }} onClick={(e) => e.stopPropagation()}>
          <button title="View" onClick={() => onView(member)} className="as-action-btn"
            style={{ display: "flex", alignItems: "center", padding: "5px 8px", background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 6, cursor: "pointer", color: "#3b82f6" }}>
            <EyeIcon />
          </button>
          <button title={isIncluded ? "Edit Priority" : "Add to Flow"} onClick={() => onEditPriority(member)} className="as-action-btn"
            style={{ display: "flex", alignItems: "center", padding: "5px 8px", background: "#fef9c3", border: "1px solid #fde68a", borderRadius: 6, cursor: "pointer", color: "#b45309" }}>
            <StarIcon />
          </button>
          <button title="Upload Signature" onClick={() => onUploadSignature(member)} className="as-action-btn"
            style={{ display: "flex", alignItems: "center", padding: "5px 8px", background: "#f5f3ff", border: "1px solid #ddd6fe", borderRadius: 6, cursor: "pointer", color: "#6d28d9" }}>
            <UploadIcon />
          </button>
        </div>
      </div>
    </div>
  );
};

const TableSkeleton = () => (
  <>
    {Array.from({ length: 4 }).map((_, i) => (
      <tr key={i} style={{ borderBottom: "1px solid #f1f5f9" }}>
        {[30, 200, 120, 100, 80, 100, 100].map((w, j) => (
          <td key={j} style={{ padding: "14px 16px" }}><SkeletonBlock w={w} h={13} /></td>
        ))}
      </tr>
    ))}
  </>
);
const CardSkeleton = () => (
  <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 16, position: "relative", overflow: "hidden" }}>
    <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: "#f1f5f9" }} />
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14, paddingTop: 4 }}>
      <SkeletonBlock w={40} h={40} r={20} />
      <div style={{ flex: 1 }}>
        <SkeletonBlock w="70%" h={14} />
        <div style={{ marginTop: 5 }}><SkeletonBlock w="50%" h={11} /></div>
      </div>
      <SkeletonBlock w={80} h={24} r={20} />
    </div>
    <div style={{ display: "flex", gap: 8, paddingTop: 10, borderTop: "1px solid #f1f5f9" }}>
      <SkeletonBlock w={80} h={24} r={6} />
      <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
        <SkeletonBlock w={32} h={28} r={6} />
        <SkeletonBlock w={32} h={28} r={6} />
        <SkeletonBlock w={32} h={28} r={6} />
      </div>
    </div>
  </div>
);

const StatsBar = ({ members, loading }) => {
  const total     = members.length;
  const approvers = members.filter((m) => m.included_in_approval_flow).length;
  const withSig   = members.filter((m) => m.digital_signature_url).length;
  const excluded  = total - approvers;

  const StatCard = ({ label, value, color, bg, border }) => (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 16px", borderRadius: 10, background: bg, border: `1px solid ${border}`, flex: "1 1 110px", minWidth: 0 }}>
      <div style={{ width: 8, height: 8, borderRadius: "50%", background: color, flexShrink: 0 }} />
      <div>
        <div style={{ fontSize: 18, fontWeight: 800, color: "#0f172a", lineHeight: 1.1 }}>
          {loading ? <SkeletonBlock w={28} h={16} /> : value}
        </div>
        <div style={{ fontSize: 11.5, color: "#64748b", fontWeight: 550, marginTop: 2, letterSpacing: ".01em" }}>{label}</div>
      </div>
    </div>
  );

  return (
    <div style={{ display: "flex", gap: 10, marginBottom: 18, flexWrap: "wrap" }}>
      <StatCard label="Total Members" value={total}     color="#6366f1" bg="#f5f3ff" border="#e0e7ff" />
      <StatCard label="In Flow"       value={approvers} color="#16a34a" bg="#f0fdf4" border="#bbf7d0" />
      <StatCard label="Excluded"      value={excluded}  color="#dc2626" bg="#fff5f5" border="#fecaca" />
      <StatCard label="With Signature" value={withSig}  color="#7c3aed" bg="#f5f3ff" border="#ddd6fe" />
    </div>
  );
};

const ApprovalFlowPreview = ({ members }) => {
  const ordered = members
    .filter((m) => m.included_in_approval_flow && m.approval_priority != null)
    .sort((a, b) => a.approval_priority - b.approval_priority);

  if (ordered.length === 0) return null;

  return (
    <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: "16px 20px", marginBottom: 18, boxShadow: "0 1px 3px rgba(0,0,0,.04)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
        <div style={{ width: 30, height: 30, borderRadius: 8, background: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center", color: "#16a34a" }}>
          <ShieldIcon />
        </div>
        <div>
          <p style={{ margin: 0, fontSize: 13.5, fontWeight: 700, color: "#0f172a" }}>Approval Flow Order</p>
          <p style={{ margin: "1px 0 0", fontSize: 12, color: "#64748b" }}>{ordered.length} approver{ordered.length !== 1 ? "s" : ""} in sequence</p>
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 0, flexWrap: "wrap", rowGap: 10 }}>
        {ordered.map((m, idx) => {
          const [bg, tc] = getAvatarColor(m.user_id);
          return (
            <div key={m.user_id} style={{ display: "flex", alignItems: "center" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 10px", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 9 }}>
                <div style={{ width: 26, height: 26, borderRadius: "50%", background: bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 800, color: tc, flexShrink: 0 }}>
                  {getInitials(m.name || m.email)}
                </div>
                <div>
                  <p style={{ margin: 0, fontSize: 12, fontWeight: 700, color: "#0f172a", whiteSpace: "nowrap" }}>{m.name || m.email}</p>
                  <p style={{ margin: 0, fontSize: 10.5, color: "#94a3b8" }}>Priority #{m.approval_priority}</p>
                </div>
                {m.digital_signature_url && (
                  <span style={{ width: 16, height: 16, borderRadius: "50%", background: "#dcfce7", display: "flex", alignItems: "center", justifyContent: "center", color: "#16a34a", flexShrink: 0 }}>
                    <CheckIcon />
                  </span>
                )}
              </div>
              {idx < ordered.length - 1 && (
                <div style={{ padding: "0 6px", color: "#94a3b8", fontSize: 16, fontWeight: 700, flexShrink: 0 }}>→</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const ApprovalSettings = ({ vendorProfileId }) => {
  const [members,       setMembers]       = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [vendorError,   setVendorError]   = useState(null);
  const [isMobile,      setIsMobile]      = useState(window.innerWidth < 768);

  const [viewMember,      setViewMember]      = useState(null);
  const [priorityMember,  setPriorityMember]  = useState(null);
  const [signatureMember, setSignatureMember] = useState(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fetchMembers = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setVendorError(null);
    try {
      const { data } = await approvalApi.list(vendorProfileId);

      if (data?.status === 401) { toast.error("Session Expired", "Please log in again."); setLoading(false); return; }
      if (data?.status === 403) { toast.error("Access Denied", "You don't have permission to view approval settings."); setLoading(false); return; }

      if (data?.status >= 400) {
        const msg = data?.data?.detail || `Error ${data?.status}`;
        if (data?.status === 404) { setVendorError(msg); }
        else { toast.error("Failed to Load", msg); }
        setMembers([]);
        setLoading(false);
        return;
      }

      setMembers(data?.results ?? []);
      if (silent) toast.success("Refreshed", "Approval settings have been updated.");
    } catch (err) {
      toast.error(
        err?.name === "TypeError" ? "Network Error" : "Failed to Load",
        err?.name === "TypeError" ? "Unable to reach the server. Check your connection." : "Unable to fetch approval settings. Please try again."
      );
    } finally {
      setLoading(false);
    }
  }, [vendorProfileId]);

  useEffect(() => { fetchMembers(); }, [fetchMembers]);

  const updateMemberInList = (updated) => {
    setMembers((prev) =>
      prev.map((m) => m.user_id === updated.user_id ? { ...m, ...updated } : m)
    );
  };

  const COLS = ["#", "Member", "Designation", "Flow Status", "Priority", "Signature", "Actions"];

  return (
    <div style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif", color: "#1e293b" }}>
      <style>{`
        @keyframes as-shimmer  { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
        @keyframes as-fadeUp   { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:none} }
        @keyframes as-alIn     { from{opacity:0;transform:translateX(24px) scale(.97)} to{opacity:1;transform:none} }
        @keyframes as-alOut    { from{opacity:1} to{opacity:0;transform:translateX(28px) scale(.95)} }
        @keyframes as-spin     { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        .as-row:hover          { background:#f8fafc!important; }
        .as-card:hover         { box-shadow:0 6px 24px rgba(0,0,0,.09)!important; border-color:#cbd5e1!important; }
        .as-action-btn:hover   { opacity:.8!important; }
        * { box-sizing:border-box; }
      `}</style>

      <ToastContainer />

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: "#0f172a", letterSpacing: "-.01em" }}>Approval Setting</h2>
          <p style={{ margin: "3px 0 0", fontSize: 13, color: "#64748b" }}>Configure the approval flow and digital signatures for vendor members</p>
        </div>
        <button onClick={() => fetchMembers(true)} disabled={loading}
          style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", background: "#fff", color: "#374151", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: loading ? "not-allowed" : "pointer", boxShadow: "0 1px 3px rgba(0,0,0,.05)", transition: "background .15s" }}
          onMouseEnter={(e) => { if (!loading) e.currentTarget.style.background = "#f1f5f9"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; }}>
          {loading ? <LoaderIcon size={15} /> : <RefreshIcon />} Refresh
        </button>
      </div>

      {vendorError && !loading && (
        <div style={{ background: "#fff5f5", border: "1px solid #fecaca", borderRadius: 12, padding: "22px 24px", display: "flex", alignItems: "flex-start", gap: 14, marginBottom: 20 }}>
          <div style={{ color: "#dc2626", flexShrink: 0, marginTop: 2 }}><AlertTriangleIcon size={22} /></div>
          <div>
            <p style={{ margin: "0 0 4px", fontSize: 15, fontWeight: 700, color: "#dc2626" }}>Vendor Profile Not Found</p>
            <p style={{ margin: 0, fontSize: 13.5, color: "#64748b" }}>{vendorError}</p>
          </div>
        </div>
      )}

      {!vendorError && (
        <>
          <StatsBar members={members} loading={loading} />

          {!loading && members.some((m) => m.included_in_approval_flow) && (
            <ApprovalFlowPreview members={members} />
          )}

          {!loading && members.length > 0 && !members.some((m) => m.included_in_approval_flow) && (
            <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 10, padding: "12px 16px", marginBottom: 18, display: "flex", alignItems: "center", gap: 10 }}>
              <AlertTriangleIcon size={16} />
              <p style={{ margin: 0, fontSize: 13, color: "#92400e" }}>
                No members have been added to the approval flow yet. Click the <strong>★ star</strong> icon on any member to set their approval priority.
              </p>
            </div>
          )}

          {!isMobile && (
            <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,.05)", animation: "as-fadeUp .25s ease" }}>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                  <thead>
                    <tr>
                      {COLS.map((h) => (
                        <th key={h} style={{ padding: "11px 16px", textAlign: "left", fontSize: 11.5, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: ".05em", background: "#f8fafc", borderBottom: "1px solid #e2e8f0", whiteSpace: "nowrap" }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <TableSkeleton />
                    ) : members.length === 0 ? (
                      <tr>
                        <td colSpan={COLS.length}>
                          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "56px 20px", gap: 10, textAlign: "center" }}>
                            <div style={{ width: 56, height: 56, borderRadius: "50%", background: "linear-gradient(135deg,#f1f5f9,#e2e8f0)", display: "flex", alignItems: "center", justifyContent: "center", color: "#94a3b8" }}>
                              <ShieldIcon />
                            </div>
                            <p style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#374151" }}>No Members Found</p>
                            <p style={{ margin: 0, fontSize: 13, color: "#94a3b8" }}>No members are associated with this vendor profile.</p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      members.map((member, idx) => (
                        <MemberRow
                          key={member.user_id}
                          member={member}
                          index={idx}
                          onView={(m) => setViewMember(m)}
                          onEditPriority={(m) => setPriorityMember(m)}
                          onUploadSignature={(m) => setSignatureMember(m)}
                        />
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {isMobile && (
            <div style={{ display: "flex", flexDirection: "column", gap: 12, animation: "as-fadeUp .25s ease" }}>
              {loading
                ? Array.from({ length: 3 }).map((_, i) => <CardSkeleton key={i} />)
                : members.length === 0
                ? (
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "56px 20px", background: "#fff", borderRadius: 12, border: "1px dashed #e2e8f0", gap: 10, textAlign: "center" }}>
                      <div style={{ width: 56, height: 56, borderRadius: "50%", background: "linear-gradient(135deg,#f1f5f9,#e2e8f0)", display: "flex", alignItems: "center", justifyContent: "center", color: "#94a3b8" }}>
                        <ShieldIcon />
                      </div>
                      <p style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#374151" }}>No Members Found</p>
                      <p style={{ margin: 0, fontSize: 13, color: "#94a3b8" }}>No members associated with this vendor profile.</p>
                    </div>
                  )
                : members.map((member) => (
                    <MemberCard
                      key={member.user_id}
                      member={member}
                      onView={(m) => setViewMember(m)}
                      onEditPriority={(m) => setPriorityMember(m)}
                      onUploadSignature={(m) => setSignatureMember(m)}
                    />
                  ))
              }
            </div>
          )}
        </>
      )}

      {viewMember && (
        <ViewDetailModal
          member={viewMember}
          vendorProfileId={vendorProfileId}
          onClose={() => setViewMember(null)}
          onEditPriority={(m) => { setViewMember(null); setPriorityMember(m); }}
          onUploadSignature={(m) => { setViewMember(null); setSignatureMember(m); }}
        />
      )}

      {priorityMember && (
        <PriorityModal
          member={priorityMember}
          vendorProfileId={vendorProfileId}
          onClose={() => setPriorityMember(null)}
          onSuccess={(updated) => {
            updateMemberInList(updated);
            setPriorityMember(null);
          }}
        />
      )}

      {signatureMember && (
        <SignatureUploadModal
          member={signatureMember}
          vendorProfileId={vendorProfileId}
          onClose={() => setSignatureMember(null)}
          onSuccess={(updated) => {
            updateMemberInList(updated);
            setSignatureMember(null);
          }}
        />
      )}
    </div>
  );
};

export default ApprovalSettings;